import { useTheme } from '@mui/material';
import { format, isFuture } from 'date-fns';
import { fr } from 'date-fns/locale';
import { type ReactNode, useMemo } from 'react';

import { resourcesColors } from '../../../theme/tokens/resourcesColors';
import type { ResourceMenuItem } from '../../organisms/ResourceMenuActions';

export type EventItem = {
  id?: string;
  title: string;
  date: string | Date;
  endDate?: string | Date | null;
  end_date?: string | Date | null;
  allDay?: boolean | null;
  all_day?: boolean | null;
  providerName?: string | null;
  provider_name?: string | null;
  contactName?: string | null;
  contact_name?: string | null;
  contactAvatarUrl?: string | null;
  contact_avatar_url?: string | null;
  description?: string | null;
  accentColor?: string;
  isImportant?: boolean | null;
  is_important?: boolean | null;
};

export type EventCardAction = ResourceMenuItem;

export type EventCardProps = {
  event: EventItem;
  action?: ReactNode;
  actions?: (EventCardAction | null | undefined | false)[];
  actionsButtonTooltip?: string;
  actionsButtonAriaLabel?: string;
  renderDescription?: (description: string) => ReactNode;
  onClick?: () => void;
  'data-testid'?: string;
};

export function useEventCard(props: EventCardProps) {
  const theme = useTheme();

  const defaultAccentColor =
    theme.palette.rendezVous?.main ??
    theme.palette.resourcesColors?.rendezVous?.main ??
    resourcesColors.rendezVous.main;

  const accentColor = props.event.accentColor ?? defaultAccentColor;

  const dateRaw = props.event.date;
  const dateObj = typeof dateRaw === 'string' ? new Date(dateRaw) : dateRaw;

  const endDateRaw = props.event.endDate ?? props.event.end_date;
  const endDateObj = endDateRaw
    ? typeof endDateRaw === 'string'
      ? new Date(endDateRaw)
      : endDateRaw
    : null;

  const isAllDay = Boolean(props.event.allDay ?? props.event.all_day);

  const rawDay = format(dateObj, 'EEEE', { locale: fr });
  const dayName = rawDay.charAt(0).toUpperCase() + rawDay.slice(1);

  const fullDate = format(dateObj, 'd MMMM yyyy', { locale: fr });

  let timeText = '';
  if (isAllDay) {
    timeText = 'Journée entière';
  } else if (endDateObj) {
    timeText = `${format(dateObj, 'HH:mm')} - ${format(endDateObj, 'HH:mm')}`;
  } else {
    timeText = format(dateObj, 'HH:mm');
  }

  const providerName = props.event.providerName ?? props.event.provider_name ?? null;
  const contactName = props.event.contactName ?? props.event.contact_name ?? null;
  const contactAvatarUrl = props.event.contactAvatarUrl ?? props.event.contact_avatar_url ?? null;
  const description = props.event.description ?? null;
  const isInFuture = isFuture(dateObj);
  const isImportant = Boolean(props.event.isImportant ?? props.event.is_important);

  const actions = useMemo(() => {
    return (props.actions ?? []).filter((action): action is EventCardAction => Boolean(action));
  }, [props.actions]);

  const actionsButtonTooltip = props.actionsButtonTooltip ?? "Options de l'événement";
  const actionsButtonAriaLabel = props.actionsButtonAriaLabel ?? actionsButtonTooltip;

  return {
    accentColor,
    dayName,
    fullDate,
    timeText,
    providerName,
    contactName,
    contactAvatarUrl,
    description,
    isInFuture,
    isImportant,
    actions,
    hasActions: actions.length > 0,
    actionsButtonTooltip,
    actionsButtonAriaLabel,
  };
}
